/**
 * Tutoriais de primeira visita, um por tela. Cada passo destaca o elemento com
 * `data-tour="<target>"` (sem alvo, o cartão aparece no centro).
 */
export interface TourStep {
  target?: string;
  title: string;
  body: string[];
}

export const TOURS = {
  lobby: [
    {
      title: 'Bem-vindo ao High Command',
      body: [
        'Estratégia global em turnos longos: 4 a 12 governos disputam o mapa-múndi por semanas.',
        'Você entra algumas vezes por dia, dá ordens secretas aos seus territórios e espera a virada do turno. Economia, pesquisa e espionagem correm em tempo real.',
      ],
    },
    {
      target: 'lobby-new',
      title: 'Crie uma partida',
      body: [
        'Escolha número de governos, turnos por dia e modo de vitória. Depois de criar, você vai para o lobby: compartilhe o código de convite e, quando quiser, inicie só com os inscritos ou completando as vagas com bots.',
        'Para aprender, uma "partida rápida de playtest" com bots tem turnos de 5 minutos.',
      ],
    },
    {
      target: 'lobby-list',
      title: 'Partidas abertas',
      body: [
        'Entre numa partida aberta (ou numa fechada, pelo código de convite) ou abra uma em andamento. No lobby você define o nome, as cores e a bandeira do seu governo.',
        'Para entrar numa partida é preciso confirmar o e-mail.',
      ],
    },
    {
      target: 'topbar-nav',
      title: 'Simulador e conta',
      body: [
        'O simulador de combate calcula batalhas com as regras do jogo, sem afetar nada.',
        'Em Conta você pode rever os tutoriais. Procure o ícone "i" ao lado das opções para ver explicações.',
      ],
    },
  ],
  match: [
    {
      title: 'Sua partida começou',
      body: [
        'Você recebeu uma capital consolidada, 40 soldados, um Quartel, um Armazém, um Centro de pesquisa, 2.000 $ e um estoque inicial (inclusive aço e combustível).',
        'Recursos e impostos entram aos poucos durante o turno no multiplayer, ou de uma vez na virada no single player. No mapa, o painel Objetivos mostra metas com recompensa.',
        'Os territórios começam neutros: conquiste os vizinhos, consolide o governo neles e faça a economia crescer. Nos 3 primeiros turnos ninguém pode atacar outro governo.',
      ],
    },
    {
      target: 'match-nav',
      title: 'Seções',
      body: [
        'Mapa e ordens, territórios (estoque, construção e produção), lista de ordens e missões, pesquisa, logística, inteligência, diplomacia, notícias e placar.',
      ],
    },
    {
      target: 'match-treasury',
      title: 'Tesouro',
      body: [
        'Dinheiro nacional. Vem dos impostos dos territórios consolidados, que entram ao longo do turno no multiplayer ou na virada no single player ("+N $/turno"), e paga pesquisa, construção, produção e manutenção. Não deixe ficar negativo.',
      ],
    },
    {
      target: 'match-specials',
      title: 'Ordens especiais (★)',
      body: [
        'Versões fortes das ordens. A quantidade por turno é o nível do Estado-Maior na capital — construa-o para ganhar ordens ★.',
      ],
    },
    {
      target: 'match-turn',
      title: 'Virada do turno',
      body: [
        'Até a virada você pode mudar as ordens livremente. Na virada todas são resolvidas ao mesmo tempo e a edição do jornal é publicada.',
      ],
    },
    {
      title: 'Avisos',
      body: [
        'Depois de cada virada aparece o resumo do turno: ataques que você sofreu, resultado dos seus ataques e notícias sobre o seu governo.',
        'Durante o turno, avisos no canto da tela informam radar, espiões, guerra e propostas. Dá para desligar em Conta.',
      ],
    },
    {
      target: 'match-help',
      title: 'Ajuda',
      body: [
        'Este botão repete o tutorial da tela atual. Os ícones "i" explicam cada função e configuração.',
      ],
    },
  ],
  map: [
    {
      target: 'map-canvas',
      title: 'A carta de situação',
      body: [
        'Azul é seu, verde aliado, vermelho inimigo; listrado indica território ocupado (ainda não consolidado).',
        'Pela neblina de guerra, dos vizinhos você vê só a faixa de tropas. Clique num território para ver o que se sabe dele.',
      ],
    },
    {
      target: 'map-oob',
      title: 'Ordem de batalha',
      body: [
        'Lista os locais com unidades suas. Clique num deles para centralizar o mapa e abrir o painel de ordem.',
      ],
    },
    {
      target: 'map-panel',
      title: 'Dar uma ordem',
      body: [
        'Escolha o tipo (Marcha, Defesa, Apoio, Saque ou Consolidar), o destino — os que estão ao alcance ficam em âmbar no mapa — e as unidades.',
        'A correlação de forças estima o resultado. Clique em Salvar ordem para gravá-la no servidor; depois de salvar, o território é desmarcado.',
      ],
    },
    {
      target: 'map-objectives',
      title: 'Objetivos',
      body: [
        'Metas da primeira fase com recompensa: expandir, construir a primeira base, chegar à Era 2, vencer outro governo e dominar um continente (que também dá bônus por turno).',
      ],
    },
    {
      target: 'map-log',
      title: 'Registro e ordens do turno',
      body: ['Eventos em tempo real e o resumo das ordens já dadas neste turno.'],
    },
    {
      target: 'map-layers',
      title: 'Camadas',
      body: ['Ligue e desligue unidades, ordens, missões e logística, zonas marítimas, rótulos e grade.'],
    },
  ],
  orders: [
    {
      target: 'orders-list',
      title: 'Ordens do turno',
      body: [
        'Todas as ordens dadas neste turno, com os avisos (ex.: guarnição abaixo da exigida). Edite ou cancele antes da virada.',
      ],
    },
    {
      target: 'orders-missions',
      title: 'Missões aéreas e de mísseis',
      body: [
        'Aeronaves e mísseis agem por missões a partir de uma base. O mapa da missão mostra o alcance e a rota; clique num alvo válido para escolhê-lo.',
      ],
    },
    {
      target: 'orders-combats',
      title: 'Relatório de combates',
      body: ['Depois da virada, veja forças, vencedor e perdas dos combates em que você participou.'],
    },
  ],
  territories: [
    {
      target: 'territories-list',
      title: 'Seus territórios',
      body: [
        'Ocupado produz 50% e não paga impostos; consolidado (Controle 100) produz tudo, paga impostos e constrói qualquer instalação.',
        'A produção entra ao longo do turno (multiplayer) ou na virada (single player). Domine um continente inteiro para ganhar o bônus de continente.',
      ],
    },
    {
      target: 'territories-stock',
      title: 'Estoque local',
      body: [
        'Os recursos ficam no território que os produziu. Para construir ou produzir aqui, eles precisam estar aqui — traga-os com rotas de logística.',
      ],
    },
    {
      target: 'territories-facilities',
      title: 'Instalações',
      body: [
        'Construa e melhore instalações (níveis 1 a 5). A construção leva tempo real e usa o estoque do território.',
      ],
    },
    {
      target: 'territories-production',
      title: 'Produção',
      body: ['Produza unidades nas instalações militares. Cada instalação produz um item por vez, em fila.'],
    },
  ],
  research: [
    {
      target: 'research-tree',
      title: 'Árvore de pesquisa',
      body: [
        'Pesquisas liberam unidades e instalações. São 4 eras; para liberar uma era conclua 60% da anterior.',
        'Cada Centro de pesquisa faz uma pesquisa por vez (máximo 3 centros).',
      ],
    },
  ],
  logistics: [
    {
      target: 'logistics-new',
      title: 'Rotas',
      body: [
        'Leve recursos de onde são produzidos para onde serão usados: rodovia, ferrovia, aéreo ou marítimo.',
        'Rotas recorrentes repetem todo turno — "50 AÇO por turno" ou "tudo acima de 100".',
      ],
    },
    {
      target: 'logistics-shipments',
      title: 'Cargas em trânsito',
      body: ['Cargas avançam na virada e podem ser capturadas ou afundadas no caminho.'],
    },
  ],
  intel: [
    {
      target: 'intel-agent',
      title: 'Agentes',
      body: [
        'Com uma Agência de inteligência, envie agentes para infiltrar, interceptar ordens, sabotar, fomentar revolta ou roubar tecnologia.',
      ],
    },
    {
      target: 'intel-recon',
      title: 'Reconhecimento',
      body: [
        'Blindados, aviões e helicópteros de reconhecimento revelam tropas e instalações em tempo real — e podem ser abatidos.',
      ],
    },
    {
      target: 'intel-reports',
      title: 'Relatórios',
      body: ['Tudo o que você descobriu, com a idade de cada dado. Informação velha pode enganar.'],
    },
  ],
  diplomacy: [
    {
      target: 'diplomacy-governments',
      title: 'Relações',
      body: [
        'Todos começam neutros (não é possível atacar). Declare guerra, proponha não-agressão ou aliança.',
        'Guerra só vale a partir do próximo turno e vira manchete.',
      ],
    },
    {
      target: 'diplomacy-trade',
      title: 'Comércio',
      body: ['Troque recursos e dinheiro com outros governos. Os recursos viajam pela logística.'],
    },
    {
      target: 'diplomacy-chat',
      title: 'Mensagens',
      body: ['Canal global, da aliança e mensagens diretas.'],
    },
  ],
  news: [
    {
      target: 'news-edition',
      title: 'O Correio da Guerra',
      body: [
        'Cada turno gera uma edição com o que foi público: conquistas, batalhas, declarações de guerra, revoltas.',
        'Leia as notícias para saber o que acontece longe das suas fronteiras.',
      ],
    },
  ],
  score: [
    {
      target: 'score-table',
      title: 'Placar',
      body: [
        'Pontuação de Poder, PIB e poder militar de cada governo, conforme o modo de vitória da partida.',
      ],
    },
  ],
  government: [
    {
      target: 'government-editor',
      title: 'Seu governo',
      body: ['Nome, tipo, cores e bandeira aparecem no mapa e nas notícias.'],
    },
  ],
  simulator: [
    {
      target: 'simulator-sides',
      title: 'Simulador de combate',
      body: [
        'Monte as tropas de cada lado e os modificadores; o simulador roda o combate várias vezes com as regras do jogo e mostra a distribuição dos resultados.',
      ],
    },
  ],
} satisfies Record<string, TourStep[]>;

export type TourId = keyof typeof TOURS;

/** Tutorial de cada seção da partida (segmento da rota). */
export const TOUR_BY_SECTION: Record<string, TourId> = {
  mapa: 'map',
  territorios: 'territories',
  ordens: 'orders',
  pesquisa: 'research',
  logistica: 'logistics',
  inteligencia: 'intel',
  diplomacia: 'diplomacy',
  noticias: 'news',
  placar: 'score',
  governo: 'government',
};
